import { Paperclip } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useChange, useClientRole } from '@/api';
import { useReinvoiceChange, useRetryChangeIssue, useStudioChange, useStudioMe } from '@/api/studio';
import type { ChangeDetail, ChangeEvent } from '@/api/types';
import { clientCan, studioCan } from '@/lib/access';
import { formatMoney, formatWhen } from '@/lib/format';
import { initiativeStatus, kindLabel, urgencyLabel, viaApi } from '@/lib/initiatives';
import { invoiceStatus } from '@/lib/status';
import { AttachmentChip } from './AttachmentChip';
import { QueryPending } from './QueryState';
import { ViaApi } from './ViaApi';
import { Dialog, Status } from './ui';

const changeStatus = initiativeStatus;

const nr = (n?: string | null) => (n ? `№ ${n}` : '');

/** What happened, in words, from the point of view of the one reading. */
function describe(e: ChangeEvent): { title: string; text?: string } {
  switch (e.kind) {
    case 'created':
      return { title: 'Запрос создан' };
    case 'asked':
      return { title: 'Студия задала вопрос', text: e.text };
    case 'answered':
      return { title: 'Ответ на вопрос', text: e.text };
    case 'parked':
      return { title: 'Студия отложила запрос', text: e.note };
    case 'done':
      return { title: 'Готово' };
    case 'task_created':
      return { title: `Создана задача ${e.key ?? ''}`.trim() };
    case 'estimated':
      return { title: `Оценка: ${e.price ? formatMoney(e.price) : ''}${e.days ? `, ${e.days} дн.` : ''}`, text: e.note };
    case 'in_scope':
      return { title: 'Взяли в работу без доплаты', text: e.note };
    case 'declined':
      return e.side === 'studio' ? { title: 'Студия отклонила запрос', text: e.reason } : { title: 'Клиент отказался от оценки' };
    case 'accepted':
      return { title: 'Оценка принята' };
    case 'invoice_issued':
      return { title: `Выставлен счёт ${nr(e.number)}`.trim() };
    case 'invoice_canceled':
      return { title: `Счёт ${nr(e.number)} отменён`.replace('  ', ' ') };
    case 'invoice_reissued':
      return { title: `Счёт выставлен заново: ${nr(e.number)}`.trim(), text: e.previousNumber ? `Вместо счёта ${nr(e.previousNumber)}` : undefined };
  }
}

function Timeline({ history }: { history: ChangeEvent[] }) {
  return (
    <ol className="relative ml-1.5 border-l border-line" aria-label="История запроса">
      {history.map((e, i) => {
        const d = describe(e);
        return (
          <li key={`${e.at}-${i}`} className="relative pb-4 pl-5 last:pb-0">
            <span className="absolute -left-[4.5px] top-1.5 size-2 rounded-full bg-strong" aria-hidden />
            <div className="text-sm font-medium">{d.title}</div>
            {d.text && <p className="text-sm text-muted">{d.text}</p>}
            <div className="text-xs text-faint">
              {e.by ? `${e.by.name}, ` : ''}
              {formatWhen(e.at)}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function Body({ c, studio }: { c: ChangeDetail; studio: boolean }) {
  const clientRole = useClientRole();
  const me = useStudioMe();
  const reinvoice = useReinvoiceChange();
  const retryIssue = useRetryChangeIssue();
  const label = changeStatus[c.status];
  const inv = c.invoice;
  // Earlier invoices of this request that were cancelled and reissued: plain text, they are not worth a link.
  const cancelledBefore = c.history.flatMap((e) => (e.kind === 'invoice_reissued' && e.previousNumber ? [e.previousNumber] : []));
  const money = studio ? studioCan(me?.studioRole, 'money') : clientCan(clientRole, 'money');
  return (
    <div className="space-y-5 p-5">
      <div>
        <Status tone={label.tone}>{studio ? label.studio : label.client}</Status>
        <p className="mt-1 text-xs text-faint">
          {!studio && c.author.side === 'client' && !viaApi(c) ? 'Вы' : c.author.name}, {formatWhen(c.createdAt)}
        </p>
        <ViaApi c={c} />
        {c.goal && (
          <div className="mt-3">
            <div className="text-xs text-faint">Зачем, какой результат ожидается</div>
            <p className="whitespace-pre-wrap text-sm">{c.goal}</p>
          </div>
        )}
        {c.description && <p className="mt-3 whitespace-pre-wrap text-sm">{c.description}</p>}
        {(c.urgency || (studio && c.kind)) && (
          <p className="mt-2 text-xs text-muted">
            {[c.urgency && urgencyLabel[c.urgency], studio && c.kind && kindLabel[c.kind]].filter(Boolean).join(' · ')}
          </p>
        )}
        {c.task && (
          <p className="mt-3 text-sm">
            {studio && c.taskUrl ? (
              <a href={c.taskUrl} target="_blank" rel="noreferrer" className="underline underline-offset-2">
                Задача {c.task.key} в Linear
              </a>
            ) : studio ? (
              <span>Задача {c.task.key}</span>
            ) : (
              <Link to={`/p/${c.projectId}/tasks/${c.task.id}`} className="underline underline-offset-2">
                Задача {c.task.key}
              </Link>
            )}
          </p>
        )}
        {!!c.attachments?.length && (
          <ul className="mt-3 flex flex-wrap gap-2 text-sm" aria-label="Вложения">
            {c.attachments.map((a) => (
              <li key={a.id || a.name}>
                <AttachmentChip a={a} icon={<Paperclip className="size-3.5 shrink-0 text-muted" />} className="inline-flex items-center gap-1.5 rounded bg-panel px-2 py-1 shadow-hair" />
              </li>
            ))}
          </ul>
        )}
        {c.declineReason && c.declinedBy === 'studio' && <p className="mt-3 text-sm">Причина отказа: {c.declineReason}</p>}
      </div>

      {c.estimate && (
        <div className="rounded bg-panel p-3 shadow-hair">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="num text-md font-semibold">{formatMoney(c.estimate.price)}</span>
            <span className="text-sm text-muted">{c.estimate.days} рабочих дней</span>
          </div>
          {c.estimate.note && <p className="mt-1 text-sm">{c.estimate.note}</p>}
        </div>
      )}

      {studio && c.linearPending && (
        <div className="rounded bg-panel p-3 shadow-hair" role="status">
          <p className="text-sm">Linear не ответил: задача не создана или не обновлена по решению в кабинете.</p>
          <button className="btn-secondary mt-2" disabled={retryIssue.isPending} onClick={() => retryIssue.mutate(c.id)}>
            Повторить в Linear
          </button>
          {retryIssue.error && (
            <p className="mt-2 text-sm text-overdue" role="alert">
              {retryIssue.error.message}
            </p>
          )}
        </div>
      )}

      {inv && (
        <div className="rounded bg-panel p-3 shadow-hair" aria-label="Счёт по запросу">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="font-medium">{inv.number ? `Счёт № ${inv.number}` : 'Счёт по запросу'}</span>
            <Status tone={inv.dead ? 'neutral' : inv.status === 'unknown' ? 'neutral' : invoiceStatus[inv.status as keyof typeof invoiceStatus].tone}>
              {inv.dead ? 'Отменён' : inv.status === 'unknown' ? 'Состояние неизвестно' : invoiceStatus[inv.status as keyof typeof invoiceStatus].label}
            </Status>
          </div>
          {inv.total && <p className="num mt-1 text-sm text-muted">{formatMoney(inv.total)}</p>}
          {!inv.dead && inv.status !== 'unknown' && !studio && money && (
            <Link to={`/money/${inv.id}`} className="mt-2 inline-block text-sm underline underline-offset-2">
              Открыть счёт
            </Link>
          )}
          {!inv.dead && !studio && !money && <p className="mt-2 text-sm text-muted">Счёт видят владелец и бухгалтер</p>}
          {!inv.dead && studio && (
            <Link to="/studio/money" className="mt-2 inline-block text-sm underline underline-offset-2">
              Счета
            </Link>
          )}
          {inv.dead && !studio && (
            <p className="mt-2 text-sm text-muted">
              {inv.number ? `Счёт № ${inv.number} — отменён. ` : ''}Студия выставит новый счёт — мы сообщим.
            </p>
          )}
          {!inv.dead && !studio && cancelledBefore.map((n) => (
            <p key={n} className="mt-2 text-sm text-muted">
              Счёт № {n} — отменён, вместо него выставлен этот.
            </p>
          ))}
          {inv.dead && studio && c.canReinvoice && money && (
            <>
              <p className="mt-2 text-sm text-muted">Счёт отменён, а запрос остался принятым. Новый счёт будет на ту же сумму.</p>
              <button className="btn-primary mt-2" disabled={reinvoice.isPending} onClick={() => reinvoice.mutate(c.id)}>
                Выставить счёт заново
              </button>
              {reinvoice.error && (
                <p className="mt-2 text-sm text-overdue" role="alert">
                  {reinvoice.error.message}
                </p>
              )}
            </>
          )}
        </div>
      )}

      <div>
        <h3 className="mb-3 text-sm font-semibold">История</h3>
        <Timeline history={c.history} />
      </div>
    </div>
  );
}

function Loaded({ q, title, onClose, studio }: { q: ReturnType<typeof useChange>; title: string; onClose: () => void; studio: boolean }) {
  return (
    <Dialog open onClose={onClose} title={title} wide>
      {q.data ? <Body c={q.data} studio={studio} /> : <QueryPending query={q} />}
    </Dialog>
  );
}

/** A request opened by the client: description, estimate, invoice and the whole story. */
export function ClientChangeDialog({ id, title, onClose }: { id: string; title: string; onClose: () => void }) {
  return <Loaded q={useChange(id)} title={title} onClose={onClose} studio={false} />;
}

/** The same for the studio, who can also issue the invoice again once the first was canceled. */
export function StudioChangeDialog({ id, title, onClose }: { id: string; title: string; onClose: () => void }) {
  return <Loaded q={useStudioChange(id)} title={title} onClose={onClose} studio />;
}
